import { PropType, defineComponent, getCurrentInstance, onMounted, reactive, ref } from 'vue'

import { CsExpander } from '@sunrise/components'
import { mediaQueryObserver, resizeObserver, screenSizeDetection } from '@sunrise/shared'
import { TrackingParameter, track } from '@sunrise/tracking'
import { ScreenSize } from '@sunrise/types'

export default defineComponent({
    name: 'MsExpanderBox',
    components: {
        CsExpander,
    },
    props: {
        tealiumData: {
            default: null,
            type: Object as PropType<TrackingParameter>,
        },
    },

    emits: ['update:expanderBox'],
    setup(props, { emit }) {
        let fullHeight: number
        let observerId: number

        const currentInstance = getCurrentInstance()?.proxy
        const { isScreenM } = screenSizeDetection()
        const expanderBoxContent = ref<HTMLDivElement | null>(null)
        const linesToShow = ref<number>(3)
        const showButton = ref<boolean>(true)
        const state = reactive({
            isExpanded: false,
            contentHeight: getCloseHeight(linesToShow.value),
        })

        function resize(): void {
            fullHeight = expanderBoxContent.value?.scrollHeight || 0
            updateContentHeight()
        }

        function updateContentHeight(): void {
            state.contentHeight = state.isExpanded
                ? `${expanderBoxContent.value?.scrollHeight}px`
                : getCloseHeight(linesToShow.value)
        }

        function updateLinesToShow(): void {
            linesToShow.value = isScreenM.value ? 3 : 6
        }

        function toggleFullText(): void {
            trackClick()
            state.isExpanded ? close() : open()
        }

        function startObserving(): void {
            observerId = resizeObserver.observe(expanderBoxContent.value as HTMLElement, resize)
        }

        function trackClick(): void {
            const tealiumData = {
                ...props.tealiumData,
                mslayer_element_type_text: state.isExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen',
            }
            track(tealiumData)
        }

        function getCloseHeight(linesToShow: number): string {
            fullHeight = expanderBoxContent.value?.scrollHeight || 0
            const contentHeight = linesToShow * getLineHeight()

            if (fullHeight <= contentHeight) {
                showButton.value = false
                return `${fullHeight}px`
            } else {
                showButton.value = true
                return `${linesToShow * getLineHeight()}px`
            }
        }
        function getLineHeight(): number {
            if (expanderBoxContent.value === null) {
                return 0
            }
            return parseInt(window.getComputedStyle(expanderBoxContent.value).lineHeight, 10)
        }
        async function open(): Promise<void> {
            emit('update:expanderBox')
            state.isExpanded = true
            await currentInstance?.$nextTick()
            state.contentHeight = `${expanderBoxContent.value?.scrollHeight}px`
        }

        function close(): void {
            emit('update:expanderBox')
            state.isExpanded = false
            state.contentHeight = getCloseHeight(linesToShow.value)
        }

        onMounted(() => {
            updateLinesToShow()

            fullHeight = expanderBoxContent.value?.scrollHeight || 0
            const contentHeight = linesToShow.value * getLineHeight()

            if (fullHeight < contentHeight) {
                state.contentHeight = `${fullHeight}px`
                showButton.value = false
            } else {
                state.contentHeight = `${contentHeight}px`
                showButton.value = true
            }

            if (mediaQueryObserver.getCurrentScreenSizes() < ScreenSize.M) {
                startObserving()
            }

            mediaQueryObserver.subscribe((newBreakpoints) => {
                if (newBreakpoints < ScreenSize.M) {
                    startObserving()
                } else {
                    resizeObserver.unobserve(observerId)
                }
                updateLinesToShow()
                resize()
            })
        })

        return {
            toggleFullText,
            isScreenM,
            expanderBoxContent,
            showButton,
            state,
        }
    },
})
